// RoleContext.tsx
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";
import { auth } from "../firebase";
import { User } from "firebase/auth";
import { apiClient } from "../utils/API/axios.interceptor";
import { endpoints } from "../../endpoints";
import posthog from "posthog-js";

export enum Role {
  SuperAdmin = "superAdmin",
  Admin = "admin",
  Moderator = "moderator",
  Creator = "creator",
  User = "user",
}

export enum Provider {
  Google = "google.com",
  Facebook = "facebook.com",
  Firebase = "firebase.com",
  Email = "email",
  Apple = "apple.com",
  Anonymous = "password",
}

export enum SubscriptionType {
  Paid = "paid",
  Free = "free",
  Member = "member",
}

export enum SubscriptionPackageType {
  Yearly = "yearly",
  Monthly = "monthly",
  Free = "free",
}

export enum UserStatus {
  Active = "active",
  Locked = "locked",
  Deactivate = "deactivate",
}

interface AuthContextType {
  user: User | null;
  role: Role | undefined;
  isLoggedIn: boolean;
  isLoading: boolean;
  subscription: Subscription | null;
  isPremium: boolean;
  isSubscriptionLoading: boolean;
  plan: Plan | null;
  refreshSubscription: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role | undefined>(undefined);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [isSubscriptionLoading, setIsSubscriptionLoading] =
    useState<boolean>(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  const isPremium = () => {
    if (!subscription) return false;
    return (
      subscription.status === "active" || subscription.status === "pending"
    );
  };

  const getSubscription = async () => {
    try {
      const res = await apiClient.get(endpoints.payments.subscription);
      return res.data;
    } catch (err) {
      setSubscription(null);
    }
  };

  const updateSubscription = async () => {
    setIsSubscriptionLoading(true);
    try {
      const { subscription, plan } = await getSubscription();
      setSubscription(subscription);
      setPlan(plan);
    } catch (error) {
      console.error("Error updating subscription:", error);
      setSubscription(null);
    } finally {
      setIsSubscriptionLoading(false);
    }
  };

  // Initial auth setup
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setIsLoading(true);
      if (user) {
        try {
          const idTokenResult = await user.getIdTokenResult(true);
          const { subscription, plan } = await getSubscription();
          setUser(user);
          posthog.identify(user.email!, { name: user.displayName });
          setSubscription(subscription);
          setPlan(plan);
          setRole((idTokenResult.claims.role as Role) || Role.User);
        } catch (error) {
          console.error("Error getting user role:", error);
          setRole(undefined);
        }
        setIsLoggedIn(true);
      } else {
        setUser(null);
        setRole(undefined);
        setIsLoggedIn(false);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        isLoggedIn,
        isLoading,
        subscription,
        plan,
        isPremium: isPremium(),
        refreshSubscription: updateSubscription,
        isSubscriptionLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
